import { useEffect, useState } from "react";
import { api } from "@/api/client";
import { PageHeader, KpiCard, EmptyState } from "@/components/common";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { Activity, AlertTriangle, CheckCircle2, Clock, FileCheck, Users, ListChecks, Hourglass, TrendingUp, MessageSquare } from "lucide-react";

export default function Dashboard() {
  const [summary, setSummary] = useState(null);
  const [trend, setTrend] = useState([]);
  const [perf, setPerf] = useState([]);
  const [delaySummary, setDelaySummary] = useState([]);
  const [aging, setAging] = useState({ rows: [], total_pending: 0 });

  useEffect(() => {
    api.get("/dashboard/summary").then(r => setSummary(r.data?.data || {})).catch(() => {});
    api.get("/dashboard/trend?days=14").then(r => setTrend(r.data?.data?.rows || [])).catch(() => {});
    api.get("/dashboard/performance").then(r => setPerf(r.data?.data?.top_performers || [])).catch(() => {});
    api.get("/dashboard/delay-summary").then(r => setDelaySummary(r.data?.data?.rows || [])).catch(() => {});
    api.get("/dashboard/approval-aging-by-approver").then(r => setAging(r.data?.data || { rows: [] })).catch(() => {});
  }, []);

  return (
    <div data-testid="dashboard-page">
      <PageHeader title="Executive Dashboard" breadcrumbs={["Home", "Dashboard"]} />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KpiCard label="Total Tasks" value={summary?.total_tasks ?? "—"} hint="All-time" testId="kpi-total-tasks" icon={ListChecks} />
        <KpiCard label="Pending" value={summary?.pending_tasks ?? "—"} hint="Open & active" accent="text-[#334E7B]" testId="kpi-pending" icon={Hourglass} />
        <KpiCard label="Completed" value={summary?.completed_tasks ?? "—"} hint="Closed/Done" accent="text-emerald-700" testId="kpi-completed" icon={CheckCircle2} />
        <KpiCard label="Delayed" value={summary?.delayed_tasks ?? "—"} hint="Past SLA" accent="text-red-700" testId="kpi-delayed" icon={AlertTriangle} />
        <KpiCard label="Awaiting Approval" value={summary?.awaiting_approval ?? "—"} hint="Pending approver" accent="text-violet-700" icon={FileCheck} />
        <KpiCard label="Return Pending" value={summary?.return_pending ?? "—"} hint="Awaiting confirmation" accent="text-amber-700" icon={Clock} />
        <KpiCard label="Today's Tasks" value={summary?.today_tasks ?? "—"} hint="Due today" icon={TrendingUp} />
        <KpiCard label="Active Users" value={summary?.active_users ?? "—"} hint="Currently active" icon={Users} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Trend */}
        <div className="card lg:col-span-2" data-testid="trend-chart">
          <div className="card-head">
            <h3><span className="dot" />Task Creation vs Completion <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">· 14-day</span></h3>
            <Activity size={16} className="text-slate-400" />
          </div>
          <div className="card-body h-80">
            <ResponsiveContainer>
              <LineChart data={trend} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                <CartesianGrid stroke="#D7DEEC" strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#64748B" }} stroke="#CBD5E1" />
                <YAxis tick={{ fontSize: 10, fill: "#64748B" }} stroke="#CBD5E1" allowDecimals={false} />
                <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12, border: "1px solid #D7DEEC", boxShadow: "0 4px 12px rgba(15,23,42,0.08)" }} />
                <Legend wrapperStyle={{ fontSize: 11 }} iconType="circle" />
                <Line type="monotone" dataKey="created" stroke="#334E7B" strokeWidth={2.5} dot={{ r: 3, fill: "#334E7B" }} activeDot={{ r: 5 }} />
                <Line type="monotone" dataKey="completed" stroke="#10B981" strokeWidth={2.5} dot={{ r: 3, fill: "#10B981" }} activeDot={{ r: 5 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Top performers */}
        <div className="card" data-testid="top-performers">
          <div className="card-head">
            <h3><span className="dot" />Top Performers</h3>
            <Users size={16} className="text-slate-400" />
          </div>
          <div className="card-body space-y-2.5">
            {perf.length === 0 && <div className="text-xs text-slate-500">No data</div>}
            {perf.slice(0, 7).map((p, i) => (
              <div key={p.user_id} className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2.5 min-w-0">
                  <div className={`w-7 h-7 rounded-lg flex items-center justify-center text-[11px] font-bold flex-shrink-0 ${
                    i === 0 ? "bg-amber-100 text-amber-700" :
                    i === 1 ? "bg-slate-100 text-slate-700" :
                    i === 2 ? "bg-orange-100 text-orange-700" :
                    "bg-[#334E7B] text-white"
                  }`}>{i + 1}</div>
                  <span className="font-medium text-slate-800 truncate">{p.name}</span>
                </div>
                <div className="text-right flex-shrink-0">
                  <div className="text-xs font-bold text-emerald-600">{p.completion_rate}%</div>
                  <div className="text-[10px] text-slate-500">{p.completed}/{p.total}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Approval-aging-by-approver widget */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
        <div className="card lg:col-span-2" data-testid="approval-aging-widget">
          <div className="card-head">
            <h3><span className="dot" />Approval Aging by Approver</h3>
            <div className="text-xs text-slate-500"><b className="text-slate-900">{aging.total_pending || 0}</b> pending</div>
          </div>
          <div className="card-body">
          {aging.rows.length === 0 ? (
            <EmptyState title="Inbox clear" hint="No tasks currently awaiting approval." />
          ) : (
            <div className="space-y-3">
              {aging.rows.slice(0, 6).map((a) => {
                const pct = Math.min(100, (a.max_aging_hours / 72) * 100);
                const sev = a.max_aging_hours > 48 ? "red" : a.max_aging_hours > 24 ? "amber" : "blue";
                const colorMap = { red: "bg-red-500", amber: "bg-amber-500", blue: "bg-[#334E7B]" };
                return (
                  <div key={a.approver_id} className="group" data-testid={`aging-row-${a.approver_id}`}>
                    <div className="flex items-center justify-between text-sm mb-1.5">
                      <div className="flex items-center gap-2 min-w-0">
                        <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#334E7B] to-[#334E7B] flex items-center justify-center text-white font-bold text-[11px] flex-shrink-0">
                          {a.approver_name?.[0]?.toUpperCase() || "?"}
                        </div>
                        <span className="font-medium text-slate-800 truncate">{a.approver_name}</span>
                        <span className="badge-base bg-slate-100 text-slate-600 border-slate-200 normal-case font-semibold">{a.count_pending}</span>
                      </div>
                      <div className="text-right flex-shrink-0 ml-3">
                        <div className={`text-xs font-bold ${
                          sev === "red" ? "text-red-700" : sev === "amber" ? "text-amber-700" : "text-slate-700"
                        }`}>{a.max_aging_hours}h max</div>
                        <div className="text-[10px] text-slate-500">avg {a.avg_aging_hours}h</div>
                      </div>
                    </div>
                    <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                      <div className={`h-full ${colorMap[sev]} rounded-full transition-all`} style={{ width: `${pct}%` }} />
                    </div>
                    {a.oldest_task_code && (
                      <div className="text-[10px] text-slate-500 mt-1 font-mono truncate">
                        Oldest: {a.oldest_task_code} · {a.oldest_task_title}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          </div>
        </div>

        {/* Delay attribution */}
        <div className="card">
          <div className="card-head">
            <h3><span className="dot" />Delay Owners</h3>
            <AlertTriangle size={16} className="text-slate-400" />
          </div>
          <div className="card-body h-64">
            <ResponsiveContainer>
              <BarChart data={delaySummary} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                <CartesianGrid stroke="#D7DEEC" strokeDasharray="3 3" />
                <XAxis dataKey="delay_owner" tick={{ fontSize: 10, fill: "#64748B" }} stroke="#CBD5E1" />
                <YAxis tick={{ fontSize: 10, fill: "#64748B" }} stroke="#CBD5E1" allowDecimals={false} />
                <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12, border: "1px solid #D7DEEC" }} />
                <Bar dataKey="count" fill="#334E7B" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Quick stats footer */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
        {[
          { Icon: Clock, label: "Recurring Today", v: summary?.recurring_due_today ?? 0, color: "text-[#334E7B]" },
          { Icon: FileCheck, label: "Approval Pending", v: summary?.awaiting_approval ?? 0, color: "text-violet-700" },
          { Icon: CheckCircle2, label: "Closed", v: summary?.completed_tasks ?? 0, color: "text-emerald-700" },
          { Icon: MessageSquare, label: "Active Users", v: summary?.active_users ?? 0, color: "text-slate-900" },
        ].map((s, i) => (
          <div key={i} className="erp-card p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#334E7B] text-white flex items-center justify-center flex-shrink-0">
              <s.Icon size={18} strokeWidth={1.8} />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{s.label}</div>
              <div className={`font-heading font-bold text-2xl ${s.color}`}>{s.v}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
